import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { FormFieldValue } from '../forms/FormFieldValue';
import { useMutation } from '@apollo/client/react';
import { ADD_CUSTOMER_MUTATION } from '../../mutations/CustomerMutations';
import { GET_CUSTOMERS_QUERY } from '../../queries/CustomerQueries';
import { validate as validateCustomer } from '../../models/Customer';
import CustomerForm, { CustomerFormState } from './CustomerForm';

const INITIAL_FORM_VALUES: Partial<CustomerFormState['values']> = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contactId: 0
};

export default function CustomerCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createCustomer] = useMutation(
    ADD_CUSTOMER_MUTATION, {
    refetchQueries: [{ query: GET_CUSTOMERS_QUERY }],
  });

  const [formState, setFormState] = useState<CustomerFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<CustomerFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<CustomerFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof CustomerFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<CustomerFormState['values']>) => {
        const { issues } = validateCustomer(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateCustomer(formValues);
    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }
    setFormErrors({});

    try {
      const { data: result } = await createCustomer({
        variables: {
          input: {
            id: 0,
            name: formValues.name || '',
            address: formValues.address || '',
            city: formValues.city || '',
            state: formValues.state || '',
            zip: formValues.zip || '',
            contactId: formValues.contactId || null
          }
        }
      });

      if (result?.added) {
        notifications.show('Customer created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/customers');
      } else {
        notifications.show(
          'Failed to create customer. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create customer. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createCustomer, formValues, navigate, notifications, setFormErrors]);

  return (
    <PageContainer
      title="New Customer"
      breadcrumbs={[{ title: 'Customers', path: '/dashboard/customers' }, { title: 'New' }]}
    >
      <CustomerForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
